/**
 * Raid Loading View
 *
 * Transition screen shown after raid starts, before room selection.
 * Features:
 * - Background image
 * - Loading spinner
 * - Rotating gameplay tips
 */

import { useEffect, useState } from "react";
import { useRaid, CoopRaidState } from "@/providers/raid-provider";
import { useAuth } from "@/providers/auth-provider";
import { quitRaid } from "@/services/raid/quit-duel";

/** Tips displayed during loading */
const LOADING_TIPS = [
  "Type advantages deal 2x damage - plan your team wisely!",
  "Coordinate with your team to cover each other's weaknesses.",
  "Boss rounds are tougher - save your strongest moves!",
  "Status effects can turn the tide of battle.",
  "Healing items can only be used outside of battle.",
  "Each player's vote counts in room selection.",
  "Rare rewards drop more often on higher difficulties.",
  "Syncing your attacks with teammates deals bonus damage!",
];

/** Tip rotation interval in ms */
const TIP_INTERVAL = 4000;

/** Props for overlay component */
export interface RaidLoadingOverlayProps {
  raidState?: CoopRaidState;
}

/**
 * Legacy view component (kept for backward compatibility)
 */
export const RaidLoadingView = (props: RaidLoadingOverlayProps) => {
  return <RaidLoadingOverlay {...props} />;
};

/**
 * Overlay component for unified Raid route
 */
export const RaidLoadingOverlay = ({ raidState }: RaidLoadingOverlayProps) => {
  // If no raid state provided, try to use context
  const contextRaid = useRaid();
  const state = raidState || contextRaid.raidState;
  const { getAccessToken } = useAuth();

  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [tipFading, setTipFading] = useState(false);
  const [showQuitButton, setShowQuitButton] = useState(false);

  /**
   * Rotate tips on interval
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setTipFading(true);
      setTimeout(() => {
        setCurrentTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
        setTipFading(false);
      }, 300);
    }, TIP_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  /**
   * Show quit button after 10 seconds of loading
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowQuitButton(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleQuitRaid = async () => {
    const token = getAccessToken();
    if (!token || !state.raid_id) {
      console.warn(
        "[RaidLoadingOverlay] Missing token or raid_id, unable to quit",
      );
      return;
    }

    try {
      console.log(
        "[RaidLoadingOverlay] Attempting to quit raid:",
        state.raid_id,
      );
      await quitRaid(token, state.raid_id);
      console.log("[RaidLoadingOverlay] Successfully quit raid");
    } catch (error) {
      console.error("[RaidLoadingOverlay] Failed to quit raid:", error);
    } finally {
      // Navigate to home to refresh raid status
      window.location.href = "/";
    }
  };

  return (
    <div
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 inset-0 flex flex-col items-center justify-center bg-pk-gray-1 overflow-hidden"
      style={{
        maxWidth: "480px",
        height: "min(100svh, calc(480px * 18 / 9))",
      }}
    >
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: `url('/assets/raids/backgrounds/${state.raid_type?.toLowerCase()}.webp')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/70" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-8 px-6 text-center">
        {/* Raid Info */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-pk-gray-5 text-shadow uppercase tracking-wider">
            {state.raid_name || "Preparing Raid"}
          </h1>
          {state.difficulty && (
            <p className="text-pk-yellow-3 font-bold uppercase tracking-widest text-sm">
              {state.difficulty} Mode
            </p>
          )}
        </div>

        {/* Spinner */}
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 border-4 border-pk-gray-3/30 rounded-full" />
          <div className="absolute inset-0 border-4 border-transparent border-t-pk-yellow-3 rounded-full animate-spin" />
          <div className="absolute inset-2 border-4 border-transparent border-b-pk-blue-4 rounded-full animate-spin-reverse" />
        </div>

        {/* Loading Text */}
        <p className="text-pk-gray-4 font-bold uppercase tracking-wider animate-pulse">
          Loading...
        </p>
      </div>

      {/* Tips Section - Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
        <div className="max-w-md mx-auto text-center">
          <p className="text-pk-yellow-3 text-xs font-bold uppercase tracking-widest mb-2">
            💡 Tip
          </p>
          <p
            className={`text-pk-gray-4 text-sm leading-relaxed transition-opacity duration-300 ${
              tipFading ? "opacity-0" : "opacity-100"
            }`}
          >
            {LOADING_TIPS[currentTipIndex]}
          </p>
          {showQuitButton && (
            <button
              onClick={handleQuitRaid}
              className="mt-4 px-4 py-2 bg-pk-red-4 hover:bg-pk-red-3 text-white font-bold uppercase text-sm rounded transition-colors"
            >
              Quit Raid
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
